/**
 * Suggestions as you type in a search box: teams and competitions whose
 * names match, best first, from the site's calendar directory (fetched once,
 * the first time a box gets focus). Arrow keys move through them and Enter
 * opens one; Enter with none chosen searches as usual, on the results page.
 * A competition opens its page; a team, its row on the results page.
 */
import { search } from "../core/search.ts";

/** What /api/calendars.json lists. */
interface Directory {
  series: { slug: string; shortName: string; name: string }[];
  teams: { name: string; league: string }[];
}

interface Suggestion {
  name: string;
  /** Other names it goes by ("F1"). */
  also: string[];
  /** What it is: "Competition", or a team's league. */
  kind: string;
  href: string;
}

/** How many suggestions show at once. */
const SHOWN = 8;

let loading: Promise<Suggestion[]> | undefined;

/** The directory as suggestions; fetched once, and again later if that failed. */
function suggestions(): Promise<Suggestion[]> {
  loading ??= fetch("/api/calendars.json")
    .then(async (response) => {
      if (!response.ok) throw new Error(String(response.status));
      const directory = (await response.json()) as Directory;
      const leagues = new Map(directory.series.map((s) => [s.slug, s.name]));
      return [
        ...directory.series.map((s) => ({
          name: s.name,
          also: [s.shortName],
          kind: "Competition",
          href: `/${s.slug}`,
        })),
        ...directory.teams.map((t) => ({
          name: t.name,
          also: [],
          kind: leagues.get(t.league) ?? "Team",
          href: `/search?q=${encodeURIComponent(t.name)}`,
        })),
      ];
    })
    .catch(() => {
      loading = undefined;
      return [];
    });
  return loading;
}

/** Makes one search box a combobox with a list of suggestions under it. */
function enhance(input: HTMLInputElement): void {
  const list = document.createElement("ul");
  list.id = `${input.id}-suggestions`;
  list.className = "suggestions";
  list.setAttribute("role", "listbox");
  list.setAttribute("aria-label", "Suggestions");
  list.hidden = true;
  const status = document.createElement("p");
  status.className = "visually-hidden";
  status.setAttribute("role", "status");
  input.after(list, status);

  input.setAttribute("role", "combobox");
  input.setAttribute("aria-autocomplete", "list");
  input.setAttribute("aria-controls", list.id);
  input.setAttribute("aria-expanded", "false");

  let shown: Suggestion[] = [];
  let active = -1;

  const setActive = (index: number): void => {
    active = index;
    [...list.children].forEach((option, i) => {
      option.setAttribute("aria-selected", String(i === index));
    });
    const option = list.children[index];
    if (option) {
      input.setAttribute("aria-activedescendant", option.id);
      option.scrollIntoView({ block: "nearest" });
    } else {
      input.removeAttribute("aria-activedescendant");
    }
  };

  const close = (): void => {
    list.hidden = true;
    input.setAttribute("aria-expanded", "false");
    setActive(-1);
  };

  const render = (items: Suggestion[]): void => {
    shown = items;
    list.replaceChildren(
      ...items.map((item, i) => {
        const option = document.createElement("li");
        option.id = `${list.id}-${String(i)}`;
        option.className = "suggestion";
        option.setAttribute("role", "option");
        option.setAttribute("aria-selected", "false");
        const name = document.createElement("span");
        name.className = "suggestion-name";
        name.textContent = item.name;
        const kind = document.createElement("span");
        kind.className = "suggestion-kind";
        kind.textContent = item.kind;
        option.append(name, kind);
        return option;
      }),
    );
    active = -1;
    input.removeAttribute("aria-activedescendant");
    const open = items.length > 0;
    list.hidden = !open;
    input.setAttribute("aria-expanded", String(open));
    status.textContent = input.value.trim()
      ? `${String(items.length)} suggestion${items.length === 1 ? "" : "s"}`
      : "";
  };

  const update = async (): Promise<void> => {
    const all = await suggestions();
    const query = input.value;
    if (document.activeElement !== input) return;
    render(query.trim() ? search(query, all, (s) => [s.name, ...s.also]).slice(0, SHOWN) : []);
  };

  input.addEventListener("focus", () => {
    void update();
  });
  input.addEventListener("input", () => {
    void update();
  });
  input.addEventListener("blur", close);

  input.addEventListener("keydown", (event) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      if (shown.length === 0) return;
      event.preventDefault();
      if (list.hidden) {
        list.hidden = false;
        input.setAttribute("aria-expanded", "true");
      }
      // Positions run from the box itself (-1) through each suggestion, wrapping round.
      const step = event.key === "ArrowDown" ? 1 : -1;
      const positions = shown.length + 1;
      setActive(((active + 1 + step + positions) % positions) - 1);
    } else if (event.key === "Enter") {
      const chosen = shown[active];
      if (!chosen || list.hidden) return;
      event.preventDefault();
      window.location.assign(chosen.href);
    } else if (event.key === "Escape" && !list.hidden) {
      event.preventDefault();
      close();
    }
  });

  // Pressing on a suggestion keeps focus in the box, so it doesn't close first.
  list.addEventListener("mousedown", (event) => {
    event.preventDefault();
  });
  list.addEventListener("click", (event) => {
    const option = (event.target as Element).closest(".suggestion");
    if (!option) return;
    const chosen = shown[[...list.children].indexOf(option)];
    if (chosen) window.location.assign(chosen.href);
  });
}

/** Adds suggestions to every search box on the page. */
export function setUpSearch(): void {
  for (const input of document.querySelectorAll<HTMLInputElement>("input[data-suggest]")) enhance(input);
}
